import React, { useCallback, useState, useEffect } from "react";
import { format, addDays, startOfMonth, endOfMonth } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import TimeSlotSelector from "@/components/booking-page/TimeSlotSelector";
import { ParticipantSelector } from "@/components/booking-page/ParticipantSelector";
import BookingSummary from "@/components/booking-page/BookingSummary";
import { TimeSlot, Participants, BookingState } from "@/types/bookinghome";
import { useQuery } from "@tanstack/react-query";
import { getActivitySlots, getActivityPricing, getActivityAvailability } from "@/utils/endUserAppApi";
import { Skeleton } from "@/components/ui/skeleton";
import { ActivityDetail } from "@/types/activitydetail";
import { useNavigate } from 'react-router-dom';
import { PricingResponse } from "@/types/pricing";

interface BookingCalendarProps {
    activityId: string;
    activity: ActivityDetail;
  }

// Mock activity ID - in a real app, this would come from the route params or props
// const ACTIVITY_ID = "6769b6d23aaa0f29a7995ec3";

export const BookingCalendar = ({ activityId, activity }: BookingCalendarProps) => {
  const navigate = useNavigate();
  const [bookingState, setBookingState] = useState<BookingState>({
    selectedDate: undefined,
    selectedTimeSlot: undefined,
    participants: {
      adults: 0,
      children: 0,
      students: 0,
    },
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Optimize queries with proper error handling
  const { 
    data: timeSlots, 
    isLoading: timeSlotsLoading, 
    //error: timeSlotsError 
  } = useQuery({
    queryKey: ["timeSlots", activityId, bookingState.selectedDate],
    queryFn: async () => {
      if (!bookingState.selectedDate || !activityId) {
        throw new Error("Missing required parameters");
      }
      const response = await getActivitySlots(
        activityId,
        format(bookingState.selectedDate, "yyyy-MM-dd")
      );
      
      return response.slots.map((slot): TimeSlot => ({
        id: `${slot.startTime}-${slot.endTime}`,
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: {
          total: 20, // This could come from activity.maxParticipants
          available: slot.available ? 1 : 0 // Convert boolean to number
        },
        price: {
          adult: {
            amount: pricingData?.pricing.adults.amount || 0, // Accessing adults amount
            currency: pricingData?.pricing.adults.currency || '' // Accessing adults currency
          },
          child: {
            amount: pricingData?.pricing.children.amount || 0, // Accessing children amount
            currency: pricingData?.pricing.children.currency || '' // Accessing children currency
          },
          student: {
            amount: pricingData?.pricing.students.amount || 0, // Accessing students amount
            currency: pricingData?.pricing.students.currency || '' // Accessing students currency
          }
        }
      }));
    },
    enabled: !!bookingState.selectedDate,
  });

  const { 
    data: pricingData,
    isLoading: pricingLoading 
  } = useQuery<PricingResponse>({
    queryKey: ["pricing", activityId],
    queryFn: () => getActivityPricing(activityId),
  });

  // Get availability data for the current month
  const { 
    data: availabilityData,
    isLoading: availabilityLoading,
    error: availabilityError 
  } = useQuery({
    queryKey: ["availability", activityId, currentMonth],
    queryFn: async () => {
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(addDays(currentMonth, 31)); // Get a bit more than one month
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [AVAILABILITY DEBUG] Making API call:', {
          activityId,
          startDate: format(startDate, "yyyy-MM-dd"),
          endDate: format(endDate, "yyyy-MM-dd")
        });
      }
      
      try {
        const result = await getActivityAvailability(
          activityId, 
          format(startDate, "yyyy-MM-dd"), 
          format(endDate, "yyyy-MM-dd")
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 [AVAILABILITY DEBUG] API Response:', {
            length: Array.isArray(result) ? result.length : 'Not an array',
            firstItem: Array.isArray(result) && result.length > 0 ? result[0] : 'None'
          });
        }
        
        return result;
      } catch (error) {
        console.error('❌ [AVAILABILITY DEBUG] API Error:', error);
        return [];
      }
    }
  });

 // Optimize handlers with useCallback
 const handleDateSelect = useCallback((date: Date | undefined) => {
    setBookingState(prev => ({
      ...prev,
      selectedDate: date,
      selectedTimeSlot: undefined,
    }));
  }, []);

  const handleTimeSlotSelect = useCallback((timeSlot: TimeSlot) => {
    setBookingState(prev => ({
      ...prev,
      selectedTimeSlot: timeSlot,
    }));
  }, []);

  const handleParticipantChange = useCallback((participants: Participants) => {
    setBookingState(prev => ({
      ...prev,
      participants,
    }));
  }, []);

  // Create calendar modifiers based on availability
  const getCalendarModifiers = useCallback(() => {
    if (!availabilityData || !Array.isArray(availabilityData)) return undefined;
    
    const available: Date[] = [];
    const lowAvailability: Date[] = [];
    const fullSlots: Date[] = [];
    
    availabilityData.forEach((dayAvailability: any) => {
      const date = new Date(dayAvailability.date);
      
      if (!dayAvailability.isBookable || dayAvailability.availableSpots === 0) {
        fullSlots.push(date);
      } else if (dayAvailability.availableSpots <= 5) { // Assuming total capacity ~20, so 25% = 5
        lowAvailability.push(date);
      } else {
        available.push(date);
      }
    });
    
    // Debug logging (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('Calendar modifiers:', {
        available: available.length,
        lowAvailability: lowAvailability.length,
        fullSlots: fullSlots.length
      });
    }
    
    return {
      available,
      lowAvailability,
      fullSlots
    };
  }, [availabilityData]);

  const modifiers = getCalendarModifiers();
  
  // Test mode: Add some hardcoded dates for testing if no API data
  const testModifiers = {
    available: [new Date(2025, 8, 14), new Date(2025, 8, 15), new Date(2025, 8, 16)], // Sept 14-16
    lowAvailability: [new Date(2025, 8, 18), new Date(2025, 8, 19)], // Sept 18-19
    fullSlots: [new Date(2025, 8, 21), new Date(2025, 8, 22)] // Sept 21-22
  };
  
  // Use test modifiers if no API data available
  const finalModifiers = modifiers || testModifiers;

  // Note: Using modifiersClassNames instead of modifiersStyles for better compatibility with shadcn/ui

  // Add custom CSS styles for calendar modifiers
  useEffect(() => {
    const styleId = 'booking-calendar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .rdp-day_button.available {
          background-color: #dcfce7 !important;
          color: #166534 !important;
          font-weight: 500 !important;
          border: 2px solid #86efac !important;
        }
        .rdp-day_button.lowAvailability {
          background-color: #fef3c7 !important;
          color: #92400e !important;
          font-weight: 500 !important;
          border: 2px solid #fde047 !important;
        }
        .rdp-day_button.fullSlots {
          background-color: #fee2e2 !important;
          color: #991b1b !important;
          text-decoration: line-through !important;
          opacity: 0.7 !important;
          border: 2px solid #fca5a5 !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Select Date</h2>
              
              {/* Compact Availability Legend */}
              <div className="flex items-center space-x-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-green-100 border border-green-200"></div>
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-yellow-100 border border-yellow-200"></div>
                  <span>Few left</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-red-100 border border-red-200"></div>
                  <span>Full</span>
                </div>
              </div>
            </div>
            
            <div className="rounded-md border">
                <Calendar
                  mode="single"
                  selected={bookingState.selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date()}
                  modifiers={finalModifiers}
                  modifiersClassNames={{
                    available: "!bg-green-100 !text-green-800 hover:!bg-green-200 !font-medium border-2 !border-green-300",
                    lowAvailability: "!bg-yellow-100 !text-yellow-800 hover:!bg-yellow-200 !font-medium border-2 !border-yellow-300",
                    fullSlots: "!bg-red-100 !text-red-800 hover:!bg-red-200 !line-through !opacity-60 border-2 !border-red-300"
                  }}
                  onMonthChange={setCurrentMonth}
                  initialFocus
                  fromDate={new Date()}
                />
            </div>
            
            {availabilityLoading && (
              <div className="mt-2 text-sm text-gray-500 text-center">
                Loading availability...
              </div>
            )}
            
            {/* Debug Info - Development only */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                <div>Availability: {availabilityData && Array.isArray(availabilityData) ? `${availabilityData.length} days` : 'Loading...'}</div>
                {availabilityError && <div className="text-red-500">Error: {String(availabilityError)}</div>}
              </div>
            )}
        </Card>

            {bookingState.selectedDate && (
              <Card className="p-4 animate-fade-in">
                <h2 className="text-xl font-semibold mb-4">
                  Available Time Slots for{" "}
                  {format(bookingState.selectedDate, "MMMM d, yyyy")}
                </h2>
                {timeSlotsLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : timeSlots ? (
                  <TimeSlotSelector
                    timeSlots={timeSlots}
                    selectedTimeSlot={bookingState.selectedTimeSlot}
                    onSelectTimeSlot={handleTimeSlotSelect}
                  />
                ) : (
                  <p className="text-muted-foreground">
                    No time slots available for this date.
                  </p>
                )}
              </Card>
            )}

            {bookingState.selectedTimeSlot && (
              <Card className="p-4 animate-fade-in">
                <h2 className="text-xl font-semibold mb-4">
                  Select Participants
                </h2>
                <ParticipantSelector
                participants={bookingState.participants}
                onChange={handleParticipantChange}
                maxParticipants={10} // Set a default or get from activity details
                pricing={pricingData?.pricing || { adults: { amount: 0, currency: '' }, children: { amount: 0, currency: '' }, students: { amount: 0, currency: '' } }}
                isLoading={pricingLoading}
              />
              </Card>
            )}
            
        {/* Booking Summary - Now integrated in the main flow */}
        {(bookingState.selectedDate || bookingState.selectedTimeSlot || 
          bookingState.participants.adults > 0 || bookingState.participants.children > 0 || bookingState.participants.students > 0) && (
          <Card className="p-4">
            <BookingSummary
              bookingState={bookingState}
              navigate={navigate}
              activity={activity}
              pricing={pricingData?.pricing}
            />
          </Card>
        )}
      </div>
    </div>
  );
};
